import ErrorMessage from '@components/atoms/ErrorMessage';
import FullPageLoading from '@components/molecules/FullPageLoading';
import {
  Maybe,
  useAvailableVehicleYearsQuery,
  useGetVehiclesByYearQuery,
  VehicleType,
} from '@graphql/generated';
import { QuoteInitialForm, VehicleFormResult } from '@lib/data/typesQuestions';
import FormField from 'components/atoms/FormField';
import GridBox from 'components/atoms/GridBox';
import OnboardingButtonBar from 'components/molecules/button-bar/OnboardingButtonBar';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';

interface Props {
  onSubmit: (data: VehicleFormResult) => void;
  loading: boolean;
  item: QuoteInitialForm;
}

function generateLabelValue(item?: Maybe<string | number>) {
  return item ? { label: item, value: item } : null;
}

const VehicleForm: FC<Props> = ({ onSubmit, loading, item }) => {
  const [availableVehicleYears] = useAvailableVehicleYearsQuery({
    pause: !!item.vehicleVersions?.length,
  });

  const years = !item.vehicleVersions?.length
    ? availableVehicleYears.data?.availableVehicleYears || []
    : [...new Set(item.vehicleVersions.map((i) => i.modelYear))];

  const { handleSubmit, errors, control, watch, setValue } = useForm<{
    brand: { value: string };
    model: { value: string; label: string };
    modelYear: { value: number; label: number };
  }>({
    mode: 'onChange',
    defaultValues: {
      modelYear:
        years.length === 1
          ? { label: years[0] as number, value: years[0] as number }
          : (generateLabelValue(item.vehicle?.modelYear) as {
              value: number;
              label: number;
            }),
    },
  });

  const formBrandValue = watch().brand?.value;

  const [getAvailableByYears] = useGetVehiclesByYearQuery({
    variables: {
      year: watch().modelYear?.value as number,
      type: item.vehicle?.type as VehicleType,
    },
    pause: !watch().modelYear?.value || !item.vehicle?.type,
  });

  const brands = [
    ...new Set(
      item.vehicleVersions?.length
        ? item.vehicleVersions?.map((i) => i.brand)
        : getAvailableByYears.data?.vehicles.map((i) =>
            i?.brand?.toUpperCase(),
          ),
    ),
  ].sort((a, b) => a?.localeCompare(b));

  const models = useMemo(
    () =>
      [
        ...new Set(
          item.vehicleVersions?.length
            ? item.vehicleVersions?.map((i) => i.model)
            : getAvailableByYears.data?.vehicles
                .filter(
                  (i) =>
                    i?.brand?.toUpperCase() === formBrandValue?.toUpperCase(),
                )
                .map((i) => i.model),
        ),
      ].sort((a, b) => a?.localeCompare(b)),
    [item, getAvailableByYears, formBrandValue],
  );

  useEffect(() => {
    if (models.length === 1) {
      setValue('model', { value: models[0], label: models[0] });
    }
  }, [formBrandValue, models, setValue]);

  return (
    <form
      onSubmit={handleSubmit((data) => {
        const vehicle = item.vehicleVersions?.find(
          (v) => v.model?.toLowerCase() === data.model?.value.toLowerCase(),
        );

        if (
          vehicle &&
          vehicle.modelYear &&
          vehicle?.fipe &&
          vehicle.model &&
          vehicle?.brand
        ) {
          return onSubmit({
            vehicle: vehicle as VehicleFormResult['vehicle'],
          });
        }

        const fipe = getAvailableByYears.data?.vehicles.find(
          (v) => v.model === data.model?.value,
        )?.fipe;

        if (!fipe || !data.modelYear?.value)
          throw new Error('Fipe não encontrada');

        return onSubmit({
          vehicle: {
            fipe,
            modelYear: data.modelYear?.value,
            brand: data.brand?.value,
            model: data.model?.value,
            type: item.vehicle?.type as VehicleType,
          },
        });
      })}
    >
      {(availableVehicleYears.fetching || getAvailableByYears.fetching) &&
        !item.vehicle?.modelYear && <FullPageLoading />}
      <GridBox justifyContent="center">
        <GridBox width={[1, 0.5, 0.5]}>
          <FormField style={{ width: '100%' }} label="Ano do Modelo">
            <Controller
              name="modelYear"
              control={control}
              rules={{ required: 'Precisamos do ano' }}
              render={(field) => (
                <Select
                  {...field}
                  onChange={(e) => {
                    setValue('brand', null);
                    setValue('model', null);

                    field.onChange(e);
                  }}
                  options={years
                    .sort((a, b) => (b as number) - (a as number))
                    .map((i) => ({
                      label: i,
                      value: i,
                    }))}
                  isDisabled={years.length === 1}
                  placeholder="Insira o ano do modelo"
                  noOptionsMessage={() => 'Não encontrado'}
                />
              )}
            />
          </FormField>
          <FormField style={{ width: '100%' }} label="Fabricante">
            <Controller
              name="brand"
              control={control}
              rules={{ required: 'Precisamos da marca' }}
              defaultValue={
                brands.length === 1
                  ? { label: brands[0], value: brands[0] }
                  : generateLabelValue(item.vehicle?.brand)
              }
              render={(field) => (
                <Select
                  {...field}
                  onChange={(e) => {
                    setValue('model', null);
                    setValue('brand', e.value);

                    field.onChange(e);
                  }}
                  options={brands.map((i) => ({
                    label: i,
                    value: i,
                  }))}
                  isDisabled={!watch().modelYear?.value || brands.length === 1}
                  placeholder="Selecione a marca"
                  noOptionsMessage={() => 'Não encontrado'}
                />
              )}
            />
          </FormField>

          <FormField style={{ width: '100%' }} label="Modelo">
            <Controller
              name="model"
              control={control}
              rules={{ required: 'Precisamos da versão' }}
              defaultValue={
                models.length === 1
                  ? { label: models[0], value: models[0] }
                  : null
              }
              render={(field) => (
                <Select
                  {...field}
                  onChange={(e) => {
                    setValue('model', e?.value);
                    field.onChange(e);
                  }}
                  options={models.map((i) => ({
                    label: i,
                    value: i,
                  }))}
                  isDisabled={!watch().brand?.value || models.length === 1}
                  placeholder="Selecione a versão do modelo"
                  noOptionsMessage={() => 'Não encontrado'}
                />
              )}
            />
            {(errors.model as { message?: string })?.message && (
              <ErrorMessage>
                {(errors.model as { message?: string }).message}
              </ErrorMessage>
            )}
          </FormField>
        </GridBox>
      </GridBox>
      <OnboardingButtonBar text="Continuar" loading={loading} />
    </form>
  );
};

export default VehicleForm;
